import React, { useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@material-ui/core';
import { IntlShape, injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { get, isEmpty } from 'lodash';
import { toast } from 'react-toastify';
import { useLastLocation } from 'react-router-last-location';
import { CHOICE } from 'containers/PreOpeningPage/constants';
import { CheckCircle, RadioButtonUnchecked } from '@material-ui/icons';
import SahamDrawer from 'components/SahamDrawer';
import { SagisConfig } from 'containers/AppContainer/typings';
import { translate } from 'containers/services/translate';
import messages from 'containers/RecapManagerPage/messages';
import {
  CLAIM_STATUS_SAGIS,
  MANAGEMENT_MODES,
  MATERIAL,
} from 'containers/RecapManagerPage/constants';
import {
  VALIDATION_DECISION,
  EMPTY_VALIDATION_DECISION,
  CLAIM,
} from 'containers/data-structure';
import { commonRightValidationsValidator } from 'containers/RecapManagerPage/CommonRightValidationForm';
import ForeignValidationForm, {
  foreignValidationValidator,
} from 'containers/RecapManagerPage/ForeignValidationForm';
import {
  formatClaimToOpen,
  findInfoSagisByRegistrationNumber,
  isMixedClaim,
} from 'containers/RecapManagerPage/utils';
import { pushTo } from 'containers/services/routes-helper';
import validate, { formsAreValidated } from 'containers/services/validateForm';
import { FEATURES } from 'features/features';
import * as featureService from 'features/fflip-helpers';
import { PAGES } from 'routes';
import {
  areAllVictimsGenderFilled,
  checkClassVictim,
  checkClassVictims,
  checkVictimAge,
} from 'containers/PreOpeningPage/utils';
import { removeWhiteSpaceFromString } from 'services/utils';
import { ClaimRecapType } from 'typings/claimTypes';
import { makeSelectIsProvisionalClaimExist } from 'containers/PreOpeningPage/selectors';
export interface ValidationDrawerProps {
  intl: IntlShape;
  open: boolean;
  handleToggleDrawer: any;
  managementModes: Array<SagisConfig>;
  responsabilities: Array<SagisConfig>;
  scaleCases: Array<SagisConfig>;
  circumstances: Array<SagisConfig>;
  requestOpenClaim: Function;
  requestOpenMixedClaim: Function;
  claimRecap: ClaimRecapType;
  claimId: number;
  handleOnOpeningSuccessDialog: Function;
  policyInfo: { sagis: {} };
  listing: any;
  requestSearchClaims: Function;
  dataClaimToUpdate?: any;
  setOpenFeeNoteDialog: Function;
  openClaimWithoutSagis: boolean;
  setIsProvisionalReferenceAlertOpenned?: Function;
}
const Container = styled.div`
  .fields {
    display: flex;
    flex-direction: column;
    margin-top: 0px;
  }
  .input {
    width: 327px;
    margin-bottom: 33px;
    margin-left: 24px;
    margin-right: 24px;
  }
  .label {
    font-size: 13px;
    font-family: Roboto;
  }
  .title {
    text-align: left;
    font-family: Roboto;
    font-size: 15px;
    font-weight: medium;
    letter-spacing: 0px;
    color: #2b2727;
    opacity: 1;
    text-transform: none;
  }
  .titleContainer {
    margin-top: 25px;
    margin-left: 25px;
    margin-bottom: 25px;
  }
  .textField {
    width: 327px;
  }
  .MuiFormLabel-root {
    font-size: 13px;
    color: #4f4f4f;
  }
  .MuiInputLabel-shrink {
    color: #00000061;
  }
  .MuiFormLabel-root.Mui-focused {
    color: rgba(9, 122, 194, 1);
  }
`;

const ValidationDrawer: React.FC<ValidationDrawerProps> = (
  props,
): JSX.Element => {
  const [decision, updateDecision] = useState(EMPTY_VALIDATION_DECISION);

  const [decisionValidation, setDecisionValidation] = React.useState(false);
  const contractualValidator = (
    decision: any,
    isChangeNatureEnabled: boolean,
    isConfirmationPreopeningEnabled: boolean,
  ) => {
    return validate(decision, {
      [VALIDATION_DECISION.CHANGE_NATURE]: {
        presence: isChangeNatureEnabled,
      },
      [VALIDATION_DECISION.CONFIRMATION_PREOPENING]: {
        presence: isConfirmationPreopeningEnabled,
      },
    });
  };
  const fraudInfo = get(props.claimRecap, 'claim.fraudInfo[0]');
  const isFraudInfoValid =
    get(props.claimRecap, 'claim.suspicious') === CHOICE.YES
      ? get(fraudInfo, 'opposingLawyer') &&
        !isEmpty(get(fraudInfo, 'fraudMotifs')) &&
        !isEmpty(get(fraudInfo, 'victims'))
      : true;

  const isClassKeyVictimsValid = !checkClassVictims(
    get(props.claimRecap, 'victims'),
    get(props.claimRecap, 'claim.lossDate'),
  );

  const priorityReasonClaimId = useSelector((state: any) =>
    get(state.toJS(), ['recap', 'claimRecap', 'claim', 'priorityReasonId']),
  );

  const handleChange = (field: string) => (
    event: React.ChangeEvent<{ name?: string; value: any }>,
  ) => {
    const { value } = event.target;
    let expectedDecision: any = { ...decision };
    expectedDecision[field] = value;
    updateDecision(expectedDecision);
    const managementMode = get(expectedDecision, [
      VALIDATION_DECISION.MANAGEMENT_MODE,
    ]);
    let expectedDecisionValidation =
      contractualValidator(
        expectedDecision,
        isChangeNatureEnabled,
        isConfirmationPreopeningEnabled,
      ) || {};
    if (managementMode === MANAGEMENT_MODES.COMMON_RIGHT) {
      expectedDecisionValidation =
        commonRightValidationsValidator(
          expectedDecision,
          isChangeNatureEnabled,
          isConfirmationPreopeningEnabled,
        ) || {};
    } else if (managementMode === MANAGEMENT_MODES.FOREIGN) {
      expectedDecisionValidation =
        foreignValidationValidator(
          expectedDecision,
          isChangeNatureEnabled,
          isConfirmationPreopeningEnabled,
        ) || {};
    }
    setDecisionValidation(formsAreValidated(expectedDecisionValidation));
  };

  const isProvisionalClaimExist = useSelector(
    makeSelectIsProvisionalClaimExist(),
  );

  const handleValidateDrawer = () => {
    const claimToOpen = formatClaimToOpen(
      {
        ...props.claimRecap,
        documents: get(props.dataClaimToUpdate, 'documents', []),
        deletedDocuments: get(props.dataClaimToUpdate, 'deletedDocuments', []),
      },
      decision,
      mixedClaim,
      props.listing,
      get(props.claimRecap, 'claim.suspicious') ? CHOICE.YES : CHOICE.NO,
      priorityReasonClaimId,
      props.openClaimWithoutSagis,
    );
    if (
      featureService.hasFeature(FEATURES.COURT_CALENDAR) &&
      claimToOpen.claim[CLAIM.IS_PROVISIONAL] === CHOICE.YES &&
      isProvisionalClaimExist === false
    ) {
      return props.setIsProvisionalReferenceAlertOpenned(true);
    }
    mixedClaim && isMixedClaim(mixedClaim)
      ? props.requestOpenMixedClaim(
          claimToOpen,
          props.claimId,
          openClaimSuccessCallback,
        )
      : props.requestOpenClaim(
          claimToOpen,
          props.claimId,
          openClaimSuccessCallback,
        );
  };

  const openClaimSuccessCallback = () => {
    if (featureService.hasFeature(FEATURES.EVOLUTIONS_FEE_NOTE)) {
      toast.success(translate(messages.validationSuccessMessage, props));
      props.handleToggleDrawer();
      props.setOpenFeeNoteDialog(true);
    } else {
      if (featureService.hasFeature(FEATURES.ASSIGNMENT_JURISDICTION_LAWYER)) {
        toast.success(translate(messages.validationSuccessMessage, props));
        return pushTo(PAGES.MANAGER_OPENED_CLAIMS)();
      }
      if (featureService.hasFeature(FEATURES.COURT_MANAGER_ASSIGNMENT)) {
        props.handleOnOpeningSuccessDialog(true);
      } else {
        const lastLocationPath = get(lastLocation, 'pathname', '');
        if (lastLocationPath) {
          pushTo(lastLocationPath)();
        }
        props.handleToggleDrawer();
        toast.success(translate(messages.validationSuccessMessage, props));
      }
    }
  };

  const renderErrorMessage = () => {
    return get(props.claimRecap, 'victims', [])?.map((el: any) => {
      if (
        checkVictimAge(el?.dateOfBirth, get(props.claimRecap, 'claim.lossDate'))
      ) {
        return translate(messages.birthDateValidationClaim, props, {
          fullName: `${removeWhiteSpaceFromString(
            el.firstName,
          )} ${removeWhiteSpaceFromString(el.lastName)}`,
        });
      } else if (
        checkClassVictim(
          el?.dateOfBirth,
          get(props.claimRecap, 'claim.lossDate'),
          el?.classKey,
        )
      ) {
        return translate(messages.classKeyValidationClaim, props, {
          fullName: `${removeWhiteSpaceFromString(
            el.firstName,
          )} ${removeWhiteSpaceFromString(el.lastName)}`,
        });
      } else if (!areAllVictimsGenderFilled([el])) {
        return translate(messages.validationSexVictim, props, {
          fullName: `${removeWhiteSpaceFromString(
            el.firstName,
          )} ${removeWhiteSpaceFromString(el.lastName)}`,
        });
      } else {
        return null;
      }
    });
  };

  const lastLocation = useLastLocation();
  const mixedClaim = (findInfoSagisByRegistrationNumber(
    (props.policyInfo as unknown) as {
      sagis: {
        claims: {
          code: string;
        }[];
      };
    },
    props.claimRecap,
  ) as unknown) as { claimType: string; status: string; doesPVExists: string };

  const inputLabel = React.useRef<HTMLLabelElement>(null);

  const isChangeNatureEnabled = mixedClaim && mixedClaim.claimType === MATERIAL;

  const isConfirmationPreopeningEnabled =
    mixedClaim &&
    (mixedClaim.claimType === MATERIAL || !mixedClaim.doesPVExists) &&
    (mixedClaim.status === CLAIM_STATUS_SAGIS.sansSuite ||
      mixedClaim.status === CLAIM_STATUS_SAGIS.clos);

  return (
    <SahamDrawer
      open={props.open}
      handleToggleDrawer={props.handleToggleDrawer}
      handleValidateDrawer={handleValidateDrawer}
      title={translate({ ...messages.validationDrawerTitle }, props)}
      isSaveButtonDisabled={
        !decisionValidation ||
        !isFraudInfoValid ||
        !isClassKeyVictimsValid ||
        !areAllVictimsGenderFilled(get(props.claimRecap, 'victims'))
      }
      confirmButtonTitle={translate(messages.drawerConfirmButtonTitle, props)}
    >
      <Container>
        <div className="titleContainer">
          <span className="title">
            {translate({ ...messages.validationDrawerSubTitle }, props)}
          </span>
        </div>
        <div className="titleContainer">
          <span style={{ color: 'red' }}>
            {get(props.claimRecap, 'claim.suspicious') === CHOICE.YES &&
            isEmpty(get(fraudInfo, 'fraudMotifs'))
              ? translate(messages.fraudMotifRequiredTitle, props)
              : get(props.claimRecap, 'claim.suspicious') === CHOICE.YES &&
                !get(fraudInfo, 'opposingLawyer')
              ? translate(messages.fraudMotifOpposingLawyer, props)
              : get(props.claimRecap, 'claim.suspicious') === CHOICE.YES &&
                isEmpty(get(fraudInfo, 'victims'))
              ? translate(messages.fraudVictimsRequiredTitle, props)
              : null}
          </span>
          <span style={{ color: 'red' }}>{renderErrorMessage()}</span>
        </div>
        <div className="fields">
          <FormControl className="input">
            <InputLabel ref={inputLabel} id="management-mode" className="label">
              {translate({ ...messages.managementMode }, props)}
            </InputLabel>
            <Select
              name="management-mode"
              value={get(decision, [VALIDATION_DECISION.MANAGEMENT_MODE])}
              onChange={handleChange(VALIDATION_DECISION.MANAGEMENT_MODE)}
            >
              {!isEmpty(props.managementModes) &&
                props.managementModes.map(mode => (
                  <MenuItem value={mode.ItemKey} key={mode.ItemKey}>
                    {mode.ItemDescription}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          {(get(decision, [VALIDATION_DECISION.MANAGEMENT_MODE]) ===
            MANAGEMENT_MODES.COMMON_RIGHT ||
            get(decision, [VALIDATION_DECISION.MANAGEMENT_MODE]) ===
              MANAGEMENT_MODES.FOREIGN) && (
            <ForeignValidationForm
              scaleCases={props.scaleCases}
              updateDecision={updateDecision}
              decision={decision}
              circumstances={props.circumstances}
              responsabilities={props.responsabilities}
              updateDecisionValidation={setDecisionValidation}
              isChangeNatureEnabled={isChangeNatureEnabled}
              isConfirmationPreopeningEnabled={isConfirmationPreopeningEnabled}
            />
          )}
          {isChangeNatureEnabled && (
            <div className="titleContainer">
              <span className="title">
                {translate(
                  { ...messages.validationMixedSinistreSubtitle },
                  props,
                )}
              </span>
              <FormControl>
                <RadioGroup
                  row
                  onChange={handleChange(VALIDATION_DECISION.CHANGE_NATURE)}
                  value={get(decision, [VALIDATION_DECISION.CHANGE_NATURE])}
                >
                  <FormControlLabel
                    control={
                      <Radio
                        color="primary"
                        value={CHOICE.YES}
                        checkedIcon={<CheckCircle />}
                        icon={<RadioButtonUnchecked />}
                        disabled={false}
                      />
                    }
                    label={translate({ ...messages.yes }, props)}
                  />
                  <FormControlLabel
                    control={
                      <Radio
                        color="primary"
                        value={CHOICE.NO}
                        checkedIcon={<CheckCircle />}
                        icon={<RadioButtonUnchecked />}
                        disabled={false}
                      />
                    }
                    label={translate({ ...messages.no }, props)}
                  />
                </RadioGroup>
              </FormControl>
            </div>
          )}
          {isConfirmationPreopeningEnabled && (
            <div className="titleContainer">
              <span className="title">
                {translate({ ...messages.confirmationPreopening }, props)}
              </span>
              <FormControl>
                <RadioGroup
                  row
                  onChange={handleChange(
                    VALIDATION_DECISION.CONFIRMATION_PREOPENING,
                  )}
                  value={get(decision, [
                    VALIDATION_DECISION.CONFIRMATION_PREOPENING,
                  ])}
                >
                  <FormControlLabel
                    control={
                      <Radio
                        color="primary"
                        value={CHOICE.YES}
                        checkedIcon={<CheckCircle />}
                        icon={<RadioButtonUnchecked />}
                        disabled={false}
                      />
                    }
                    label={translate({ ...messages.yes }, props)}
                  />
                  <FormControlLabel
                    control={
                      <Radio
                        color="primary"
                        value={CHOICE.NO}
                        checkedIcon={<CheckCircle />}
                        icon={<RadioButtonUnchecked />}
                        disabled={false}
                      />
                    }
                    label={translate({ ...messages.no }, props)}
                  />
                </RadioGroup>
              </FormControl>
            </div>
          )}
        </div>
      </Container>
    </SahamDrawer>
  );
};

export default injectIntl(ValidationDrawer);
